import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { accounts as accountsApi } from "../lib/supabase.js";

export default function Accounts() {
  const navigate = useNavigate();
  const [accts, setAccts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", domain: "", plan: "professional", ai_provider: "openai", widget_color: "#6366f1" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await accountsApi.list();
    setAccts(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setSaving(true);
    const { data, error } = await accountsApi.create({
      ...form,
      ai_model: form.ai_provider === "openai" ? "gpt-4o-mini" : form.ai_provider === "gemini" ? "gemini-2.0-flash" : "claude-sonnet-4-5-20250929",
      status: "onboarding",
    });
    if (error) { alert(error.message); setSaving(false); return; }
    setShowCreate(false);
    setForm({ name: "", slug: "", domain: "", plan: "professional", ai_provider: "openai", widget_color: "#6366f1" });
    setSaving(false);
    load();
  };

  const inputStyle = { width: "100%", padding: "10px 14px", background: "#06060a", border: "1px solid #1f1f3a", borderRadius: 8, color: "#e0e0e0", fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 14 };
  const labelStyle = { display: "block", fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontWeight: 600 };

  return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Accounts</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#555" }}>{accts.length} total</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: "10px 20px", background: "linear-gradient(135deg, #6366f1, #4f46e5)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ New Account</button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowCreate(false)}>
          <div style={{ background: "#0d0d12", border: "1px solid #1f1f3a", borderRadius: 16, padding: 32, width: 440 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 24px", fontSize: 20, color: "#e0e0e0" }}>New Account</h2>
            <label style={labelStyle}>Company Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} placeholder="Acme Industries" style={inputStyle} />
            <label style={labelStyle}>Slug</label>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="acme-industries" style={{ ...inputStyle, fontFamily: "monospace" }} />
            <label style={labelStyle}>Domain</label>
            <input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="acme.com" style={inputStyle} />
            <div style={{ display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Plan</label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} style={inputStyle}>
                  <option value="professional">Professional — $299/mo</option>
                  <option value="enterprise">Enterprise — $499/mo</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>AI Provider</label>
                <select value={form.ai_provider} onChange={(e) => setForm({ ...form, ai_provider: e.target.value })} style={inputStyle}>
                  <option value="openai">OpenAI</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>
            </div>
            <label style={labelStyle}>Widget Color</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <input type="color" value={form.widget_color} onChange={(e) => setForm({ ...form, widget_color: e.target.value })} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
              <span style={{ fontFamily: "monospace", fontSize: 13, color: "#888" }}>{form.widget_color}</span>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: "10px 20px", background: "transparent", border: "1px solid #1f1f3a", borderRadius: 8, color: "#888", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.name || !form.slug} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #6366f1, #4f46e5)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>{saving ? "Creating..." : "Create Account"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Account Cards */}
      {loading ? <div style={{ color: "#555" }}>Loading...</div> : (
        <div style={{ display: "grid", gap: 14 }}>
          {accts.map((a) => (
            <div key={a.id} onClick={() => navigate(`/accounts/${a.id}`)} style={{ background: "#0a0a0f", border: "1px solid #1a1a2e", borderRadius: 12, padding: 24, cursor: "pointer", transition: "border-color 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "#2a2a4a"} onMouseLeave={(e) => e.currentTarget.style.borderColor = "#1a1a2e"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: `${a.widget_color || "#6366f1"}20`, border: `1px solid ${a.widget_color || "#6366f1"}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: a.widget_color || "#6366f1" }}>{a.name?.[0]}</div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 600 }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{a.domain || a.slug} · {a.ai_provider}/{a.ai_model}</div>
                  </div>
                </div>
                <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "uppercase", background: a.status === "active" ? "#0a2e1a" : "#2a1f05", color: a.status === "active" ? "#34d399" : "#fbbf24", border: `1px solid ${a.status === "active" ? "#166534" : "#854d0e"}` }}>{a.status}</span>
              </div>
              <div style={{ display: "flex", gap: 24, fontSize: 12, color: "#555" }}>
                <span>Plan: <b style={{ color: "#ccc" }}>{a.plan}</b></span>
                {a.billing?.[0] && <span>MRR: <b style={{ color: "#34d399" }}>${(a.billing[0].mrr_cents / 100).toLocaleString()}</b></span>}
                <span>Created: {new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
